import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LaunchScreen } from "@/components/loading/LaunchScreen";
import { MaintenanceScreen } from "@/components/maintenance/MaintenanceScreen";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { forceBetaAcknowledgmentReauth, hardReloadToBetaGate, hasForcedBetaAcknowledgmentReauth } from "@/lib/betaAcknowledgmentGate";
import { migrateLegacyAccessCache, touchAccessCache, logOfflineBootstrapPath, writeAccessCache, readValidAccessCacheAsync } from "@/lib/offlineAccessCache";
import { markCheckoutSettling, isCheckoutSettling } from "@/lib/checkoutReturn";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

type AccessCacheEntry = { hasAccess: boolean; checkedAt: number };

// Allow cache is intentionally short-lived because it lives in localStorage and
// could be tampered with (an authenticated user could set hasAccess:true to
// skip the beta/legal acknowledgment gate on offline cold starts). Server-side
// RLS still protects data; this cache only affects the UI shell + legal gate.
// Keep this window short enough that any tampering quickly self-heals on the
// next online launch.
const ACCESS_CACHE_TTL_MS = 1000 * 60 * 60; // 1 hour

// Deny cache is short and ONLY used to prevent redirect/loading loops for non-approved users.
const DENY_CACHE_TTL_MS = 1000 * 30; // 30 seconds

// Bump CACHE_BUST to force-invalidate all access caches across all clients on deploy.
// (e.g. after legal/acknowledgment gate changes that must take effect immediately.)
// v4: fixes accepted beta applicants being cached as allowed before their
// beta_tester record exists and before the acknowledgment gate can run.
// v5: force ALL existing beta testers back through the acknowledgment gate.
// v6: remove cached-access rendering before the beta acknowledgment check finishes.
// v7: force unsigned beta testers to re-authenticate before signing acknowledgment.
// v8: remove in-memory allow shortcut so already-open PWAs cannot stay past the gate.
// v9: replace persistent beta reauth flag with fresh sign-in-only session marker.
const CACHE_BUST = "v9";

// Use localStorage for access cache (persists across PWA refreshes)
// Use sessionStorage only for deny cache (should reset on new session)
const ACCESS_CACHE_STORAGE_PREFIX = `llx:access:${CACHE_BUST}:`;
const DENY_CACHE_STORAGE_PREFIX = `llx:access-deny:${CACHE_BUST}:`;

const accessCache = new Map<string, AccessCacheEntry>();
const denyCache = new Map<string, AccessCacheEntry>();

const readStoredEntry = (storageKey: string, ttlMs: number, useLocalStorage = false): AccessCacheEntry | undefined => {
  try {
    const storage = useLocalStorage ? localStorage : sessionStorage;
    const raw = storage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as AccessCacheEntry;

    if (
      typeof parsed?.hasAccess !== "boolean" ||
      typeof parsed?.checkedAt !== "number"
    ) {
      storage.removeItem(storageKey);
      return undefined;
    }

    if (Date.now() - parsed.checkedAt >= ttlMs) {
      storage.removeItem(storageKey);
      return undefined;
    }

    return parsed;
  } catch {
    return undefined;
  }
};

const getCacheEntry = (
  cache: Map<string, AccessCacheEntry>,
  userId: string,
  storagePrefix: string,
  ttlMs: number,
  useLocalStorage = false
): AccessCacheEntry | undefined => {
  const mem = cache.get(userId);
  if (mem && Date.now() - mem.checkedAt < ttlMs) return mem;

  const stored = readStoredEntry(`${storagePrefix}${userId}`, ttlMs, useLocalStorage);
  if (stored) cache.set(userId, stored);
  return stored;
};

const setCacheEntry = (
  cache: Map<string, AccessCacheEntry>,
  userId: string,
  storagePrefix: string,
  hasAccess: boolean,
  useLocalStorage = false
) => {
  const entry: AccessCacheEntry = { hasAccess, checkedAt: Date.now() };
  cache.set(userId, entry);
  try {
    const storage = useLocalStorage ? localStorage : sessionStorage;
    storage.setItem(`${storagePrefix}${userId}`, JSON.stringify(entry));
  } catch {
    // ignore storage errors
  }
};

// Access cache uses localStorage (persists across PWA refreshes)
const getAccessCache = (userId: string) =>
  getCacheEntry(accessCache, userId, ACCESS_CACHE_STORAGE_PREFIX, ACCESS_CACHE_TTL_MS, true);

const setAccessCache = (userId: string, hasAccess: boolean) =>
  setCacheEntry(accessCache, userId, ACCESS_CACHE_STORAGE_PREFIX, hasAccess, true);

const persistVerifiedAccess = (userId: string) => {
  setAccessCache(userId, true);
  writeAccessCache(userId);
};

const getDenyCache = (userId: string) =>
  getCacheEntry(denyCache, userId, DENY_CACHE_STORAGE_PREFIX, DENY_CACHE_TTL_MS);

const setDenyCache = (userId: string) =>
  setCacheEntry(denyCache, userId, DENY_CACHE_STORAGE_PREFIX, false);

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const {
    user,
    session,
    loading,
    isAuthReady,
    bootstrapStatus,
    bootstrapReason,
    retryAuthInit,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const hasSession = !!session?.user;
  const effectiveUser = user ?? session?.user ?? null;
  const userId = effectiveUser?.id ?? null;

  // Maintenance mode check - admins bypass
  const { isMaintenanceMode, checked: maintenanceChecked } = useMaintenanceMode(userId ?? undefined);

  // If we've already verified access for this user recently, avoid showing a loading
  // screen during route transitions (prevents a visible flash on "Back to Home").
  const userEmail = effectiveUser?.email ?? null;

  // Check cache SYNCHRONOUSLY on every render - this is the fast path for PWA navigation
  // CRITICAL: Only use cache for the CURRENT user - never trust other users' cache entries
  const getCachedAccessStatus = (): { hasValidCachedAllow: boolean; hasValidCachedDeny: boolean } => {
    // If we don't have a userId yet, DON'T trust any cached access.
    // This prevents granting access to new users based on a previous user's cache.
    if (!userId) {
      return { hasValidCachedAllow: false, hasValidCachedDeny: false };
    }

    // Self-heal: promote any still-valid legacy v2..v8 entry to v9 before reading.
    migrateLegacyAccessCache(userId);

    const cachedAllow = getAccessCache(userId);
    const cachedDeny = getDenyCache(userId);
    
    const hasValidCachedAllow = !!(
      cachedAllow?.hasAccess && Date.now() - cachedAllow.checkedAt < ACCESS_CACHE_TTL_MS
    );

    const hasValidCachedDeny = !!(
      cachedDeny && Date.now() - cachedDeny.checkedAt < DENY_CACHE_TTL_MS
    );
    
    return { hasValidCachedAllow, hasValidCachedDeny };
  };

  const { hasValidCachedAllow, hasValidCachedDeny } = getCachedAccessStatus();

  // Legal/beta gates must fail closed: cached access can speed verification,
  // but it must never render protected content before acknowledgment is checked.
  const [verifying, setVerifying] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [hydrationStalled, setHydrationStalled] = useState(false);
  
  // Timeout to prevent infinite loading - if auth doesn't resolve, redirect to auth
  // Use 30s for cross-browser/mobile resilience (WebKit/Firefox can be slow)
  const [timedOut, setTimedOut] = useState(false);

  // Prevent re-running expensive checks for the same user during rapid auth events.
  const verified = useRef<{ userId: string; hasAccess: boolean } | null>(null);

  // Avoid redirect races right after sign-in/sign-up where Supabase session exists
  // but AuthContext hasn't published `user` yet.
  const sessionRetryCount = useRef(0);

  // Timeout to prevent infinite loading states (30 seconds - increased for cross-browser)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isAuthReady || verifying) {
        logger.warn('[ProtectedRoute] Auth timed out after 30s, redirecting to auth');
        setTimedOut(true);
      }
    }, 30000);
    
    return () => clearTimeout(timeout);
  }, [isAuthReady, verifying]);

  // After auth bootstraps: if there's no session, this is an unauthenticated user.
  // Redirect to /auth (but NEVER redirect if a session exists — prevents false negatives on WebKit/mobile).
  // EXCEPTION: If offline with no cached session, DON'T redirect - show the offline first-launch message instead.
  useEffect(() => {
    if (!isAuthReady || loading) return;
    // Offline first-launch: don't redirect, let BootstrapGate show the message
    if (bootstrapReason === 'offline_no_session') {
      if (import.meta.env.DEV) {
        logger.log('[ProtectedRoute] Offline first-launch detected, not redirecting');
      }
      return;
    }
    if (!hasSession && bootstrapStatus === 'ready' && bootstrapReason === 'no_session') {
      if (import.meta.env.DEV) {
        logger.log('[ProtectedRoute] Redirecting to /auth: confirmed_no_session');
      }
      navigate('/auth', { replace: true });
    }
  }, [isAuthReady, loading, hasSession, bootstrapStatus, bootstrapReason, navigate]);

  useEffect(() => {
    let cancelled = false;

    const verifyAccess = async () => {
      // CRITICAL: Wait until auth is truly ready before making any decisions
      if (!isAuthReady || loading) return;
      // If there is no session, the unauthenticated redirect effect above will handle it.
      if (!hasSession) return;
      if (cancelled) return;

      // Offline relaunch fast-path: after a user has passed beta/legal gates online,
      // use the short-lived local allow cache so airplane-mode starts can render
      // cached app data instead of being held on the butterfly restore screen.
      if (!navigator.onLine && hasValidCachedAllow && userId) {
        logger.log('[ProtectedRoute] Offline with verified access cache; rendering cached app shell');
        // Slide the 7-day window forward on real usage and emit a telemetry beacon.
        touchAccessCache(userId);
        logOfflineBootstrapPath("offline_cache_hit", { source: "protected_route" });
        if (!cancelled) {
          verified.current = { userId, hasAccess: true };
          setHydrationStalled(false);
          setHasAccess(true);
          setVerifying(false);
        }
        return;
      }

      if (!navigator.onLine && userId) {
        const indexedAccess = await readValidAccessCacheAsync(userId);
        if (cancelled) return;
        if (indexedAccess) {
          logger.log('[ProtectedRoute] Offline with IndexedDB verified access cache; rendering cached app shell');
          touchAccessCache(userId);
          logOfflineBootstrapPath("offline_cache_hit", { source: "protected_route_indexeddb" });
          verified.current = { userId, hasAccess: true };
          setHydrationStalled(false);
          setHasAccess(true);
          setVerifying(false);
          return;
        }
      }

      // During fast auth transitions, `user` can be temporarily null while a session already exists.
      if (!user || !userId || !userEmail) {
        if (!navigator.onLine && session?.user) {
          sessionRetryCount.current = 0;
          if (!cancelled) {
            setHydrationStalled(true);
            setVerifying(false);
          }
          return;
        }

        const { data: { session: fetchedSession } } = await supabase.auth.getSession();
        if (cancelled) return;

        if (fetchedSession?.user) {
          // Give AuthContext a moment to publish the user.
          // Retry up to 25 times (5 seconds total) instead of bouncing back to /auth.
          // This is critical for cross-browser session rehydration timing.
          if (sessionRetryCount.current < 25) {
            sessionRetryCount.current += 1;
            logger.log('[ProtectedRoute] Session exists but user not in context, retry:', sessionRetryCount.current);
            setTimeout(() => {
              if (!cancelled) void verifyAccess();
            }, 200);
            return;
          }
          logger.warn('[ProtectedRoute] Exhausted session retries, session exists but user never populated');
          if (!cancelled) {
            setHydrationStalled(true);
            setVerifying(false);
          }
          // Trigger a bootstrap retry (non-blocking)
          retryAuthInit();
          return;
        }

        sessionRetryCount.current = 0;
        // Only route to /auth when we have a confirmed no-session state.
        // Otherwise, keep the user in a safe recovery UI.
        if (bootstrapStatus === 'ready' && bootstrapReason === 'no_session') {
          if (import.meta.env.DEV) {
            logger.log('[ProtectedRoute] Redirecting to /auth: missing_user_and_no_session');
          }
          navigate("/auth", { replace: true });
        } else {
          if (!cancelled) {
            setHydrationStalled(true);
          }
        }
        if (!cancelled) setVerifying(false);
        return;
      }

      // We have a user now; reset retry counter.
      sessionRetryCount.current = 0;

      // If we already verified this user as denied, don't re-run network checks.
      // Never reuse an in-memory allow result: legal/beta gates must be rechecked
      // for already-open PWA sessions after an update.
      if (verified.current?.userId === userId && !verified.current.hasAccess) {
        if (!cancelled) {
          setHasAccess(verified.current.hasAccess);
          setVerifying(false);
          // Non-approved authenticated users go to /marketing to avoid auth redirect loops.
          navigate("/marketing", { replace: true });
        }
        return;
      }

      // Fast-path deny cache (prevents loading/redirect loops).
      if (hasValidCachedDeny) {
        if (!cancelled) {
          verified.current = { userId, hasAccess: false };
          setHasAccess(false);
          setVerifying(false);
          // Non-approved authenticated users go to /marketing to avoid auth redirect loops.
          navigate("/marketing", { replace: true });
        }
        return;
      }

      logger.log('[ProtectedRoute] Verifying beta access for user:', userId);

      // OFFLINE FIRST-LAUNCH: If there is no valid allow cache, do NOT grant
      // optimistic app access. Beta/legal gates must fail closed until an
      // online verification confirms acknowledgment status.
      if (!navigator.onLine) {
        logger.warn('[ProtectedRoute] Offline detected without verified access cache; holding app access closed');
        logOfflineBootstrapPath("offline_cache_miss", { source: "protected_route" });
        if (!cancelled) {
          verified.current = null;
          setHasAccess(false);
          setHydrationStalled(true);
          setVerifying(false);
        }
        return;
      }

      // FIRST: Check all access sources in parallel (admin, beta tester, test account, approved application)
      // This prevents admins from being blocked when they're not in beta_applications
      const [betaTesterResult, adminResult, testAccountResult, approvedResult] = await Promise.all([
        supabase.from("beta_testers").select("id, agreement_signed_at").eq("user_id", userId).maybeSingle(),
        supabase.rpc("has_role", {
          _user_id: userId,
          _role: "admin",
        }),
        supabase
          .from("test_accounts")
          .select("id")
          .eq("user_id", userId)
          .eq("is_active", true)
          .maybeSingle(),
        supabase.rpc("is_beta_application_accepted", { p_email: userEmail }),
      ]);

      if (cancelled) return;

      const isBetaTester = !!betaTesterResult.data;
      const isAdmin = !!adminResult.data;
      const isTestAccount = !!testAccountResult.data;
      const isApproved = !!approvedResult.data;
      const betaAgreementSignedAt = betaTesterResult.data?.agreement_signed_at ?? null;

      // Check if ALL requests failed (likely network issue)
      const allFailed = betaTesterResult.error && adminResult.error && testAccountResult.error && approvedResult.error;
      if (allFailed) {
        logger.warn('[ProtectedRoute] All access checks failed; holding app access closed until beta/legal gate can be verified');
        if (!cancelled) {
          verified.current = null;
          setHasAccess(false);
          setHydrationStalled(true);
          setVerifying(false);
        }
        return;
      }

      // The beta acknowledgment gate is a legal gate. If any core gate check
      // errors, fail closed instead of treating missing data as "not beta" and
      // accidentally rendering the dashboard in a PWA with flaky storage/network.
      if (betaTesterResult.error || adminResult.error || testAccountResult.error) {
        logger.warn('[ProtectedRoute] Beta gate check failed; holding app access closed', {
          betaTesterError: betaTesterResult.error?.message,
          adminError: adminResult.error?.message,
          testAccountError: testAccountResult.error?.message,
        });
        if (!cancelled) {
          verified.current = null;
          setHasAccess(false);
          setHydrationStalled(true);
          setVerifying(false);
        }
        return;
      }

      // Log any errors but don't fail on them
      if (betaTesterResult.error) {
        logger.warn('[ProtectedRoute] Beta tester check error:', betaTesterResult.error.message);
      }
      if (adminResult.error) {
        logger.warn('[ProtectedRoute] Admin check error:', adminResult.error.message);
      }
      if (testAccountResult.error) {
        logger.warn('[ProtectedRoute] Test account check error:', testAccountResult.error.message);
      }
      if (approvedResult.error) {
        logger.warn('[ProtectedRoute] Approval check error:', approvedResult.error.message);
      }

      // Grant access if user is already a beta tester, admin, or test account
      if (isBetaTester || isAdmin || isTestAccount) {
        logger.log('[ProtectedRoute] Access granted for user:', userId, { isBetaTester, isAdmin, isTestAccount });
        // HARD GATE: non-admin beta testers must sign the Beta Participation Acknowledgment.
        // Admins and test accounts bypass. Don't redirect if already on the acknowledgment page.
        if (
          isBetaTester &&
          !isAdmin &&
          !isTestAccount &&
          !betaAgreementSignedAt
        ) {
          logger.log('[ProtectedRoute] Beta tester missing acknowledgment, gating');
          // CRITICAL: do NOT cache access yet — caching here would let a future
          // cold PWA launch fast-path past the gate before the async re-check fires.
          if (!cancelled) {
            verified.current = null;
            setHasAccess(false);
            setVerifying(false);
            if (!hasForcedBetaAcknowledgmentReauth(userId)) {
              await forceBetaAcknowledgmentReauth(userId);
              await hardReloadToBetaGate("/auth?beta_ack=required");
              return;
            }
            if (location.pathname !== "/beta-acknowledgment") {
              await hardReloadToBetaGate("/beta-acknowledgment");
            }
          }
          return;
        }
        // Only cache access once the ack gate is satisfied (or user is admin/test).
        persistVerifiedAccess(userId);
        if (!cancelled) {
          verified.current = { userId, hasAccess: true };
          setHasAccess(true);
          setVerifying(false);
        }
        return;
      }

      // Public Launch Mode: any authenticated non-beta user is allowed in.
      // Read the flag synchronously from the same sources useLaunchMode uses,
      // so we don't have to thread a hook into this large component.
      let publicLaunchEnabled = false;
      try {
        if (
          typeof window !== "undefined" &&
          (localStorage.getItem("lupuslogix_force_launch") === "1" ||
            sessionStorage.getItem("lupuslogix_force_launch") === "1")
        ) {
          publicLaunchEnabled = true;
        }
      } catch { /* ignore */ }
      if (!publicLaunchEnabled) {
        const { data: launchSetting } = await (supabase
          .from("app_settings" as any)
          .select("value")
          .eq("key", "landing_mode")
          .maybeSingle() as any);
        publicLaunchEnabled = launchSetting?.value === "full";
      }
      if (cancelled) return;

      if (publicLaunchEnabled) {
        logger.log('[ProtectedRoute] Public Launch Mode: granting access to non-beta user');
        // Guard against users who signed up but abandoned checkout: if there is
        // no active user_subscriptions row, route them to /subscription to
        // complete or start payment. Allow the /subscription, /marketing, and
        // /account pages themselves so the user can actually finish checkout
        // or manage billing. /export is exempt too: data export is included on
        // every plan and lapsed users must keep data portability (GDPR/CCPA),
        // which our cancellation email explicitly promises.
        // /trial-success is exempt too: it is the neutral post-checkout
        // confirmation screen (no wellness data). Stripe redirects there
        // immediately, several seconds BEFORE the webhook inserts the
        // user_subscriptions row, so bouncing it to /subscription both traps
        // the user behind the Back button and cancels the conversion event
        // it is responsible for firing. It redirects to
        // /dashboard?success=true itself, which re-enters the grace window.
        const checkoutExemptPaths = ["/subscription", "/marketing", "/account", "/settings", "/export", "/trial-success"];
        const isOnExemptPath = checkoutExemptPaths.some((p) => location.pathname.startsWith(p));
        // Just-returned-from-Stripe grace window: Stripe redirects to
        // /dashboard?success=true, but the webhook that inserts the
        // user_subscriptions row can lag by a few seconds. Bouncing the user
        // back to /subscription during that gap makes it look like their
        // payment didn't go through. Trust the success param and let the
        // dashboard render while the subscription row catches up.
        // The ?success=true param disappears as soon as the dashboard
        // normalizes its URL (e.g. /dashboard?tab=home), so a param-only
        // check loses the grace window on the very next verification pass and
        // bounces the user to /subscription?resume=1. Pair it with the
        // time-based settling marker (5 min TTL) written on checkout return.
        const successParam =
          new URLSearchParams(location.search).get("success") === "true";
        if (successParam) {
          try { markCheckoutSettling(); } catch { /* ignore */ }
        }
        let justReturnedFromCheckout = successParam;
        if (!justReturnedFromCheckout) {
          try { justReturnedFromCheckout = isCheckoutSettling(); } catch { /* ignore */ }
        }
        if (!isOnExemptPath && !justReturnedFromCheckout) {
          const readActivePaidRow = async () => {
            const { data: subRow } = await supabase
              .from("user_subscriptions")
              .select("tier, expires_at")
              .eq("user_id", userId)
              .maybeSingle();
            return !!(
              subRow &&
              (subRow.tier === "core" || subRow.tier === "premium" || subRow.tier === "lifetime" || subRow.tier === "upgraded") &&
              (!subRow.expires_at || new Date(subRow.expires_at as string) > new Date())
            );
          };
          let hasActivePaidRow = await readActivePaidRow();
          if (cancelled) return;
          // Second chance for webhook lag: a brand-new subscriber's row can
          // land a few seconds after the Stripe redirect. Retry once before
          // sending them back to the paywall.
          if (!hasActivePaidRow) {
            await new Promise((r) => setTimeout(r, 2500));
            if (cancelled) return;
            hasActivePaidRow = await readActivePaidRow();
            if (cancelled) return;
          }
          if (!hasActivePaidRow) {
            logger.warn('[ProtectedRoute] Public Launch: user has no active subscription, routing to /subscription');
            if (!cancelled) {
              verified.current = { userId, hasAccess: true };
              setHasAccess(true);
              setVerifying(false);
              navigate("/subscription?resume=1", { replace: true });
            }
            return;
          }
        }
        persistVerifiedAccess(userId);
        if (!cancelled) {
          verified.current = { userId, hasAccess: true };
          setHasAccess(true);
          setVerifying(false);
        }
        return;
      }

      // Pre-launch: not already granted — check if they have an approved application
      if (!isApproved) {
        logger.warn('[ProtectedRoute] User not approved for beta and has no existing access, redirecting to marketing');
        setDenyCache(userId);
        if (!cancelled) {
          verified.current = { userId, hasAccess: false };
          setHasAccess(false);
          setVerifying(false);
          // Non-approved authenticated users go to /marketing to avoid auth redirect loops.
          navigate("/marketing", { replace: true });
        }
        return;
      }

      // Approved beta applicants must be converted to beta_testers before app
      // access is granted, otherwise the acknowledgment gate cannot see them.
      // This intentionally fail-closes: if the grant cannot be verified, do not
      // cache or render protected content.
      logger.log('[ProtectedRoute] User is approved; creating/verifying beta benefits before access');
      const { error: grantError } = await supabase.rpc("grant_beta_benefits", { p_user_id: userId });

      if (cancelled) return;

      if (grantError) {
        logger.warn('[ProtectedRoute] grant_beta_benefits failed; blocking app access until retry:', grantError.message);
        verified.current = null;
        setHasAccess(false);
        setVerifying(false);
        setHydrationStalled(true);
        return;
      }

      logger.log('[ProtectedRoute] Beta benefits granted; redirecting to acknowledgment before app access');
      try {
        localStorage.removeItem(`${ACCESS_CACHE_STORAGE_PREFIX}${userId}`);
      } catch { /* ignore */ }
      accessCache.delete(userId);
      verified.current = null;
      setHasAccess(false);
      setVerifying(false);
      if (location.pathname !== "/beta-acknowledgment") {
        await hardReloadToBetaGate("/beta-acknowledgment");
      }

      return;
    };

    void verifyAccess();

    return () => {
      cancelled = true;
    };
  }, [userId, userEmail, user, session, loading, isAuthReady, hasSession, navigate, location.pathname, hasValidCachedAllow, hasValidCachedDeny]);

  // Handle timeout - redirect to auth if stuck
  useEffect(() => {
    if (!timedOut) return;
    // If the user is authenticated, do NOT redirect on timeout; keep showing loading.
    // This avoids false redirects when access checks are slow on WebKit/mobile.
    if (hasSession) {
      logger.warn('[ProtectedRoute] Timed out but session exists; staying on loading');
      return;
    }
    // Only redirect when bootstrap confirmed there is no session.
    if (bootstrapStatus === 'ready' && bootstrapReason === 'no_session') {
      logger.warn('[ProtectedRoute] Auth timed out, redirecting to auth (confirmed no session)');
      navigate('/auth', { replace: true });
      return;
    }

    // Otherwise, do not redirect—fall back to restore UI.
    logger.warn('[ProtectedRoute] Auth timed out but session is not confirmed absent; showing restore UI');
    setHydrationStalled(true);
  }, [timedOut, hasSession, bootstrapStatus, bootstrapReason, navigate]);

  // MAINTENANCE MODE: Show maintenance screen for all non-admin users
  if (maintenanceChecked && isMaintenanceMode) {
    return <MaintenanceScreen />;
  }

  // Show loading screen while auth is initializing.
  // ALWAYS wrap in protected-content for E2E stability - this ensures the marker exists
  // even during auth bootstrapping, which prevents race condition failures in E2E tests.
  if (bootstrapStatus === 'stalled' || hydrationStalled) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      logOfflineBootstrapPath("offline_stalled", {
        bootstrap_status: bootstrapStatus,
        hydration_stalled: hydrationStalled,
      });
    }
    return (
      <div data-testid="protected-content">
        <LaunchScreen subtitle="Restoring session…" onRetry={retryAuthInit} />
      </div>
    );
  }

  if (!isAuthReady || loading) {
    return (
      <div data-testid="protected-content">
        <LaunchScreen />
      </div>
    );
  }

  // When authenticated (session exists), always render a stable wrapper so E2E can reliably sync,
  // even while access verification is still running.
  if (hasSession && (timedOut || verifying)) {
    return (
      <div data-testid="protected-content">
        <LaunchScreen />
      </div>
    );
  }

  // After auth is ready: no user means redirect already happened
  if (!user) {
    return null;
  }

  // User exists but doesn't have access - redirect already happened
  if (!hasAccess) {
    return null;
  }

  logger.log('[ProtectedRoute] Rendering protected content for user:', user.id);
  // Wrap in a div with data-testid for E2E stability (no visual change)
  return <div data-testid="protected-content">{children}</div>;
};
